import { fetchOperatorConnection, operatorRequestHeaders, OperatorConnection } from '../../../../providers/operatorConnection';

/**
 * One Operator's work queue, Stage 6 of the HQ queue screens.
 *
 * **This talks to the Operator, not to HQ-API.** `fetchOperatorConnection` gets a host and an
 * Ed25519 ticket good for about a minute, and the browser calls the Operator with it - the same
 * arrangement the status, log and connection-count panels on this screen already use. A ticket is
 * fetched **per call**, never held: one taken on mount has lapsed by the time anyone presses
 * refresh, and a stored one is refused as a replay rather than merely being stale.
 *
 * The shape is the Operator's, so it is snake_case on the wire (`depth_read`, `in_flight`) where
 * HQ-API's queue report is camelCase. It is mapped to camelCase here so the components either side
 * of this file read like the rest of HQ, and so a field the Operator stops sending becomes a null
 * rather than an `undefined` halfway down a render.
 *
 * **Null is a reading and zero is a different reading**, which is the rule the whole feature is
 * built on. `depth` and `consumers` are null when the Operator could not reach the broker, and
 * `depthRead` says which of the two happened. A screen that renders both as `0` would report a
 * healthy empty queue on an Operator that has lost its broker.
 */

/** What the Operator is doing with the one job it can hold. Prefetch is 1, so there is at most one. */
export type InFlightJob = {
    /**
     * The identifier `var/log/messenger.log` uses, so a row here and a line in the log name the
     * same job.
     */
    job: string,
    attempt: number | null,
    /** The field worth showing: a job held for an hour is why the queue behind it is not moving. */
    runningForSeconds: number | null,
};

export type OperatorQueueState = {
    /** The broker queue name, which is configuration on the Operator rather than a constant here. */
    queue: string,
    /** Waiting on the broker. Null when it could not be read. */
    depth: number | null,
    /** Every consumer on that queue, across all Operators, not just this one. Null when unread. */
    consumers: number | null,
    /** Whether the two counts above are readings at all. */
    depthRead: boolean,
    connected: boolean,
    /** Whether *this* Operator is consuming. Different question from `consumers` above. */
    consuming: boolean,
    /** Shutting down: it has stopped taking jobs and is putting the one in hand back. */
    draining: boolean,
    inFlight: InFlightJob | null,
};

const text = (value: unknown): string => typeof value === 'string' ? value : '';

const countOrNull = (value: unknown): number | null =>
    typeof value === 'number' && Number.isFinite(value) ? value : null;

const toInFlight = (raw: unknown): InFlightJob | null => {
    if (typeof raw !== 'object' || raw === null) {
        return null;
    }

    const job = raw as Record<string, unknown>;

    return {
        job: text(job.job),
        attempt: countOrNull(job.attempt),
        runningForSeconds: countOrNull(job.running_for_seconds),
    };
};

const toQueueState = (raw: unknown): OperatorQueueState => {
    const data = (typeof raw === 'object' && raw !== null ? raw : {}) as Record<string, unknown>;

    return {
        queue: text(data.queue),
        depth: countOrNull(data.depth),
        consumers: countOrNull(data.consumers),
        // Only an explicit `true` counts. An older Operator that does not send the field at all
        // must read as "could not look", not as a successful reading of nothing.
        depthRead: data.depth_read === true,
        connected: data.connected === true,
        consuming: data.consuming === true,
        draining: data.draining === true,
        inFlight: toInFlight(data.in_flight),
    };
};

/**
 * Read one Operator's queue. Throws on anything that is not a 200.
 *
 * The Operator itself never fails this call over an unreachable broker - it answers 200 with
 * `depth_read: false` - so a throw here means the Operator could not be reached at all, or refused
 * the ticket. Those are worth telling an admin about in different words from an empty queue, which
 * is why this throws rather than returning a null state.
 */
export const loadOperatorQueue = async (operatorId: number | string): Promise<OperatorQueueState> => {
    const connection: OperatorConnection = await fetchOperatorConnection(operatorId);
    // The trailing slash is the route as FastAPI mounts it; without it the Operator answers a 307
    // redirect, which `fetch` follows but which drops the ticket header on some proxies.
    const response: Response = await fetch(new Request(connection.host + '/system/queue/', {
        method: 'GET',
        headers: operatorRequestHeaders(connection),
    }));

    if (!response.ok) {
        throw new Error('The Operator would not report its queue (' + response.status + ').');
    }

    return toQueueState(await response.json());
};
